import { z } from 'zod';
import { CUSTOM_STICKER_MAX } from './garage';
import { MAX_MISSIONS_PER_SHIFT } from './missions';

/** Request/response contract for /api/v1 (docs/BACKEND.md). Money is always integer pesewas. */

export const VehicleId = z.enum(['sprinter', 'hiace', 'vitz']);

export const CreatePlayerRequest = z.object({ platform: z.string().max(40).optional() });
/** Long enough for a full name, e.g. "Kwesi Obeng Asante". */
export const NAME_MAX = 24;

export const RenameRequest = z.object({
  name: z.string().trim().min(2).max(NAME_MAX).regex(/^[\p{L}\p{N} _.'-]+$/u, 'letters, numbers and spaces only'),
});
export const StartRunRequest = z.object({
  vehicle: VehicleId,
  route: z.string().regex(/^[a-z0-9-]{1,40}$/).optional(),
  /** 'daily' plays today's challenge (the server sets the road, trotro and seed). */
  challenge: z.literal('daily').optional(),
  /** Plays a friend's challenge link: same seed, road and trotro as their shift. */
  friendCode: z.string().regex(/^[a-f0-9]{8}$/).optional(),
});

const count = z.number().int().min(0).max(10_000);

/**
 * What a finished shift reports. The server works out fines and repairs itself from the offence
 * counts (so a client can't under-report the money), and sanity-checks fares against distance.
 */
export const FinishRunRequest = z.object({
  runToken: z.string().min(10).max(400),
  score: z.number().int().min(0).max(100_000_000),
  metres: z.number().int().min(0).max(1_000_000),
  durationMs: z.number().int().min(0).max(12 * 3600 * 1000),
  faresPesewas: z.number().int().min(0).max(100_000_000),
  passengersCarried: count,
  passengersDropped: count,
  closeCalls: count,
  perfectStops: count,
  /** Pesewa coins picked up on the road. */
  coins: count.max(100_000).default(0),
  offences: z.object({ redLight: count, pedestrianCrossing: count, rearEnd: count, checkpoint: count.default(0), papers: count.default(0), dashTen: count.default(0), dashTwenty: count.default(0) }),
  /** Fuel units bought at petrol stations (the server prices them). */
  fuelUnits: count.default(0),
  /** Units in the tank when the shift began (the tank carries over between shifts). */
  fuelStart: z.number().min(0).max(1000).optional(),
  /** Crashes by what was hit (e.g. { taxi: 1, scrape: 2 }). */
  repairs: z.record(z.string().max(20), count),
  endReason: z.enum(['breakdown', 'endShift']),
  /** A one-way journey was finished (reached the far end). The server checks the distance and pays the bonus. */
  arrived: z.boolean().optional(),
  /** Missions finished during this shift; the server pays the reward for each level. */
  missionsCompleted: z.array(z.object({ id: z.string().max(20), level: z.number().int().min(1).max(200) })).max(MAX_MISSIONS_PER_SHIFT).default([]),
  clientVersion: z.string().max(20).optional(),
});

export type FinishRunRequest = z.infer<typeof FinishRunRequest>;

/** A mid-shift update (every ~60 s): the shift's totals so far. Missions are only paid at the end. */
export const ProgressRunRequest = FinishRunRequest.omit({ endReason: true, missionsCompleted: true });
export type ProgressRunRequest = z.infer<typeof ProgressRunRequest>;

export type Ranks = { day: number | null; week: number | null; all: number | null; challenge: number | null };

export interface ProgressRunResponse {
  ok: true;
  accepted: boolean;
  wallet: Wallet;
  ranks: Ranks;
}

/** What starting a shift returns. For challenges, the road is decided by the server. */
export interface StartRunResponse {
  ok: true;
  runId: string;
  runToken: string;
  seed: number;
  vehicle: string;
  route: string | null;
  mode: 'normal' | 'daily' | 'friend';
  /** A friend's challenge: who, and the score to beat. */
  friend?: { name: string; score: number };
}

/** One of the challenges you sent, and how your friends got on. */
export interface MyChallenge {
  code: string;
  score: number;
  route: string | null;
  vehicle: string;
  createdAt: number;
  attempts: { name: string; score: number; beaten: boolean; at: number; isNew: boolean }[];
}

export interface ChallengeInfo {
  code: string;
  name: string;
  score: number;
  seed: number;
  route: string | null;
  vehicle: string;
  /** How many friends have tried it, and the best of them. */
  attempts: number;
  best: { name: string; score: number } | null;
}

export const BuyRequest = z.object({
  kind: z.enum(['paint', 'sticker', 'horn', 'upgrade']),
  id: z.string().max(40),
  /** Only for id 'custom' stickers. */
  text: z.string().trim().min(1).max(CUSTOM_STICKER_MAX).regex(/^[\p{L}\p{N} !'&.,-]+$/u).optional(),
});

export const AdImpressionsRequest = z.object({
  items: z
    .array(z.object({ slotId: z.string().max(40), campaignId: z.string().max(40), views: z.number().int().min(0).max(1000), seconds: z.number().min(0).max(36_000) }))
    .max(50),
});

export const AdCampaignRequest = z.object({
  id: z.string().regex(/^[a-z0-9-]{2,40}$/),
  advertiser: z.string().min(1).max(80),
  title: z.string().min(1).max(120),
  imageUrl: z.string().max(500).refine((u) => u.startsWith('/') || u.startsWith('https://'), 'use /path or https://'),
  startsAt: z.number().int(),
  endsAt: z.number().int(),
});

/** An ad creative uploaded from the admin page: a compressed image as base64 (about 150 KB is typical). */
export const AdImageRequest = z.object({
  contentType: z.enum(['image/jpeg', 'image/png', 'image/webp']),
  data: z.string().regex(/^[A-Za-z0-9+/]+={0,2}$/).max(700_000),
});

export const AdBookingRequest = z.object({
  slotId: z.string().max(40),
  campaignId: z.string().max(40),
  startsAt: z.number().int(),
  endsAt: z.number().int(),
});

export type AdCampaignRequest = z.infer<typeof AdCampaignRequest>;
export type AdBookingRequest = z.infer<typeof AdBookingRequest>;

export interface OwnedItem {
  kind: 'paint' | 'sticker' | 'horn' | 'upgrade';
  id: string;
  text?: string;
}

/** How often the game sends mid-shift progress, and the fastest the server accepts it. */
export const PROGRESS_EVERY_SECONDS = 60;
export const PROGRESS_MIN_GAP_MS = 20_000;

export interface Wallet {
  balancePesewas: number;
  lifetimeFaresPesewas: number;
  lifetimeFinesPesewas: number;
  lifetimeRepairsPesewas: number;
}

export interface LedgerEntry {
  kind: 'fare' | 'fine' | 'repair' | 'purchase' | 'reward' | 'fuel';
  detail: string;
  amountPesewas: number;
  createdAt: number;
}

export interface FinishRunResponse {
  ok: true;
  accepted: boolean;
  rejectReason?: string;
  wallet: Wallet;
  best: number;
  ranks: Ranks;
  /** Badges this shift earned (ids from badges.ts). */
  newBadges: string[];
  /** For a friend's challenge: did you beat them? */
  friend?: { name: string; score: number; beaten: boolean };
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  score: number;
  /** The rarest few badges this driver has earned (ids). */
  badges?: string[];
}

/** 'challenge' is today's daily challenge board. */
export type Period = 'day' | 'week' | 'all' | 'challenge';
export type ApiError = { ok: false; error: { code: string; message: string } };
